<template>
  <f7-block-title>{{ $t('campaign.contribute') }}</f7-block-title>

  <f7-block v-if="hasCategories" class="categories-title">
    <p class="categories-note">{{ $t('category.pickYourCategory') }}</p>
  </f7-block>
  <f7-block v-else-if="freeContributionText" class="categories-title">
    <p class="categories-note">{{ freeContributionText }}</p>
  </f7-block>

  <f7-list inset strong outline dividers media-list>
    <f7-list-item
      v-if="hasCategories"
      :title="$t('category.yourCategory')"
      smart-select
      :smart-select-params="{ openIn: 'sheet' }"
    >
      <select :value="selectedIndex" @change="onSelectCategory($event)">
        <option value="-1">{{ $t('category.choose') }}</option>
        <option v-for="(cat, i) in categoryAmounts" :key="i" :value="i">
          {{ categoryOptionLabel(cat) }}
        </option>
      </select>
    </f7-list-item>
    <f7-list-input
      :label="$t('campaign.amountCFA')"
      type="number"
      placeholder="5000"
      :readonly="amountLocked"
      :value="amount"
      @input="amount = $event.target.value"
    ><template #media><span class="currency">{{campaign?.currency}}</span></template></f7-list-input>
    <f7-list-item checkbox :title="$t('campaign.stayAnonymous')" v-model:checked="anonymous" />
  </f7-list>

  <f7-block v-if="hasCategories && lockCategoryAmounts && selectedCat" class="categories-title">
    <p class="categories-note locked-note">
      {{ $t('category.amountLockedNote', { amount: formatAmount(selectedCat.amount) }) }}
    </p>
  </f7-block>

  <f7-block>
    <f7-button large fill round raised :disabled="!canSubmit" @click="submit">
      {{ $t('campaign.contribute') }}{{ amount ? ` — ${Number(amount).toLocaleString('fr-FR')} ${campaign?.currency}` : '' }}
    </f7-button>
  </f7-block>
</template>

<script setup>
import { ref, computed } from 'vue'
import { f7 } from 'framework7-vue'
import { usePaymentTrigger } from '../../js/composables/usePaymentTrigger.js'
import i18n from '../../js/i18n.js'
import { useCampaignsStore } from '../../js/pinia-stores/campaign.js'

const { t } = i18n.global

const props = defineProps({
  campaignId: { type: String, required: true },
  groupId: { type: String, default: null },
  campaignName: { type: String, default: '' },
  groupName: { type: String, default: '' },
  campaignType: { type: String, default: 'FUND_COLLECT' },
  f7route: { type: Object, default: null },
  f7router: { type: Object, default: null },
  categoryAmounts: { type: Array, default: () => [] },
  lockCategoryAmounts: { type: Boolean, default: true },
  freeContributionText: { type: String, default: '' },
})
const emit = defineEmits(['contributed'])

const trigger = usePaymentTrigger({ f7router: props.f7router })
const amount = ref('')
const anonymous = ref(false)
const selectedIndex = ref(-1)
const campaignStore = useCampaignsStore()
const campaign = computed(() => campaignStore.activeCampaign)
const hasCategories = computed(() => (props.categoryAmounts || []).length > 0)
const selectedCat = computed(() =>
  selectedIndex.value >= 0 ? props.categoryAmounts[selectedIndex.value] : null,
)
const amountLocked = computed(() => hasCategories.value && props.lockCategoryAmounts)

const canSubmit = computed(() => {
  if (!Number(amount.value) || Number(amount.value) <= 0) return false
  if (hasCategories.value && selectedIndex.value < 0) return false
  return true
})

function formatAmount(n) {
  return `${Number(n || 0).toLocaleString('fr-FR')} CFA`
}

function categoryOptionLabel(cat) {
  const title = cat.category === 'CUSTOM' ? cat.name : cat.label
  return `${title || cat.category} — ${formatAmount(cat.amount)}`
}

function onSelectCategory(event) {
  selectedIndex.value = Number(event.target.value)
  const cat = selectedCat.value
  if (cat) {
    amount.value = cat.amount != null ? String(cat.amount) : ''
  }
}

/**
 * Hands off to the unified /pay page where the contributor picks a
 * provider (wallet, mobile money, card). The contribution record is
 * attached server-side through the PaymentTransactionRequest entity
 * context (entityType CAMPAIGN/CONTRIBUTION + campaignId).
 */
function submit() {
  try {
    const cat = selectedCat.value
    const identifier = cat.category === 'CUSTOM' ? cat.name : cat.label
    const value = hasCategories.value && props.lockCategoryAmounts && cat ? Number(cat.amount) : Number(amount.value)
    trigger.triggerCampaignContribution({
      campaignId: props.campaignId,
      groupId: props.groupId,
      amount: value,
      campaignType: props.campaignType,
      campaignName: props.campaignName,
      groupName: props.groupName,
      anonymous: anonymous.value,
      category: cat?.category || null,
      categoryLabel: identifier || null,
      tag: t('campaign.contribute'),
    })
    emit('contributed', { initiated: true })
  } catch (err) {
    f7.dialog.alert(err?.message || t('campaign.contributionFailed'))
  }
}
</script>

<style scoped>
.categories-title {
  margin: 0 16px;
}
.categories-note {
  font-size: 12px;
  color: var(--f7-on-surface-variant, #8e8e93);
  line-height: 1.4;
  margin: 4px 0;
}
.locked-note {
  font-weight: 500;
  color: var(--f7-theme-color);
}
</style>